<template>
  <v-dialog :model-value="isOpen" width="440" v-on:keydown.stop persistent>
    <template v-slot:activator="{ props }">
      <slot name="activator" :props="props"></slot>
    </template>

    <div class="dialog">
      <div class="d-flex mb-6">
        <span class="text-title mr-3 mt-3 text-truncate">Adding user(s)?</span>
        <v-spacer />
        <v-btn variant="text" color="grey" @click="close" icon="mdi-close" :disabled="loadingAddUsersToSubscription">
        </v-btn>
      </div>

      <div class="mb-6">
        You're adding {{ users?.length }} user(s).
        <span v-if="hasScheduledDowngrade && availableSeatsCount < users?.length">
          You have a scheduled seat reduction. This will increase your price from ${{ oldPrice }} to ${{ newPrice }} (excluding tax).
          <span v-if="availableSeatsCount !== 0">You are being charged less because you had {{ availableSeatsCount }} spare seat(s) available in your scheduled plan.</span>
        </span>
        <span v-else-if="availableSeatsCount < users?.length">
          This will increase your price from ${{ oldPrice }} to ${{ newPrice }} (excluding tax).
          <span v-if="availableSeatsCount !== 0">You are being charged less because you had {{ availableSeatsCount }} spare seat(s) available in your plan.</span>
        </span>
        <span v-else>
          This won't change your price because you have {{
            availableSeatsCount
          }} available seat(s) in your subscription{{ hasScheduledDowngrade ? ' (based on your scheduled plan)' : '' }}.
        </span>
      </div>

      <div class="d-flex ga-2">
        <v-spacer/>
        <v-btn
          size="large"
          variant="outlined"
          color="var(--red-1)"
          :disabled="loadingAddUsersToSubscription"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          size="large"
          variant="flat"
          :loading="loadingAddUsersToSubscription"
          @click="onAdd"
        >
          OK
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>
<script>
import store from '@/store'
import { mapState } from 'vuex'
import { getPlanByPriceId, getSubscriptionTypeByPriceId, getScheduledQuantity } from '@/utils/payments'

export default {
  name: 'add-users-to-subscription-dialog',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false
    },
    users: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState({
      subscriptionUsers: state => state.payments.subscriptionUsers,
      activeSubscription: state => state.payments.activeSubscription,
      loadingAddUsersToSubscription: state => state.payments.loadingAddUsersToSubscription
    }),
    selectedSubscriptionType: function () {
      if (this.activeSubscription) {
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id
        return getSubscriptionTypeByPriceId(priceId)
      }
      return null
    },
    selectedPlan: function () {
      if (this.activeSubscription) {
        return getPlanByPriceId(this.activeSubscription?.subscriptionDetails?.price?.id)
      }
      return null
    },
    currentPricePerUser () {
      return this.selectedSubscriptionType === 'monthly' ? this.selectedPlan?.monthlyPrice : this.selectedPlan?.yearlyPrice
    },
    hasScheduledDowngrade () {
      const scheduledQty = getScheduledQuantity(this.activeSubscription)
      const currentQty = this.activeSubscription?.subscriptionDetails?.quantity
      return scheduledQty !== undefined && scheduledQty < currentQty
    },
    availableSeatsCount () {
      const scheduledQty = getScheduledQuantity(this.activeSubscription)
      const currentQty = this.activeSubscription?.subscriptionDetails?.quantity
      const effectiveQty = scheduledQty !== undefined ? scheduledQty : currentQty
      return (effectiveQty ?? 0) - (this.subscriptionUsers?.length ?? 0)
    },
    oldPrice () {
      const scheduledQty = getScheduledQuantity(this.activeSubscription)
      const effectiveQty = scheduledQty !== undefined ? scheduledQty : this.activeSubscription.subscriptionDetails.quantity
      return this.currentPricePerUser * effectiveQty
    },
    newPrice () {
      return this.currentPricePerUser * (this.subscriptionUsers.length + this.users.length)
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    async onAdd () {
      if (this.users.length > 0) {
        const addUsersToSubscriptionResponse = await store.dispatch('payments/addUsersToSubscription', { users: this.users })

        if (addUsersToSubscriptionResponse.success) {
          this.$emit('onAdd')
        }
      } else {
        this.$emit('close')
      }
    }
  }
}
</script>

<style scoped>
.dialog {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--light-1);
  background: var(--light-1);
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.15);
}
</style>
